import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pill, ArrowLeft, Upload, FileText, Image, CreditCard, Stethoscope, Trash2, Download, Clock, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPE_LABELS = {
  lab_results:        { label: "Lab Results",          icon: FileText,    iconBg: "bg-purple-100",  iconText: "text-purple-600",  chipBg: "bg-purple-50",  chipText: "text-purple-700"  },
  photo_id:           { label: "Photo ID",             icon: Image,       iconBg: "bg-blue-100",    iconText: "text-blue-600",    chipBg: "bg-blue-50",    chipText: "text-blue-700"    },
  insurance_card:     { label: "Insurance Card",       icon: CreditCard,  iconBg: "bg-emerald-100", iconText: "text-emerald-600", chipBg: "bg-emerald-50", chipText: "text-emerald-700" },
  prior_prescription: { label: "Prior Prescription",   icon: FileText,    iconBg: "bg-amber-100",   iconText: "text-amber-600",   chipBg: "bg-amber-50",   chipText: "text-amber-700"   },
  prescriber_notes:   { label: "Prescriber Notes",     icon: Stethoscope, iconBg: "bg-amber-100",   iconText: "text-amber-600",   chipBg: "bg-amber-50",   chipText: "text-amber-700"   },
  other:              { label: "Other",                icon: FileText,    iconBg: "bg-slate-100",   iconText: "text-slate-600",   chipBg: "bg-slate-50",   chipText: "text-slate-700"   },
};

export default function PatientDocuments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState("lab_results");
  const [description, setDescription] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [docsRes, reqsRes] = await Promise.all([
        API.get("/documents/mine"),
        API.get("/document-requests/mine"),
      ]);
      setDocuments(docsRes.data);
      setRequests(reqsRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await API.post(
        `/documents/upload?document_type=${uploadType}&description=${encodeURIComponent(description)}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Document uploaded successfully!");
      setDescription("");
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async (docId) => {
    try {
      await API.delete(`/documents/${docId}`);
      toast.success("Document deleted");
      loadData();
    } catch { toast.error("Failed to delete"); }
  };

  const handleFulfill = async (reqId) => {
    try {
      await API.put(`/document-requests/${reqId}/fulfill`);
      toast.success("Request marked as fulfilled");
      loadData();
    } catch { toast.error("Failed to update"); }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await API.get(`/documents/download/${doc.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const filteredDocs = useMemo(() => filter === "all" ? documents : documents.filter(d => d.document_type === filter), [documents, filter]);
  const pendingRequests = useMemo(() => requests.filter(r => r.status === "pending"), [requests]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="patient-documents-page">
      <div className="bg-white/80 backdrop-blur-xl border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center"><Pill className="w-4 h-4 text-white" /></div>
            <span className="font-bold text-lg text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>GLP<span className="text-purple-600">Flo</span></span>
          </Link>
          <Button variant="ghost" className="rounded-full text-slate-600" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 lg:py-12">
        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>My Documents</h1>
        <p className="text-slate-600 mb-8">Upload and manage your lab results, ID, and insurance documents.</p>

        {/* Pending Requests Alert */}
        {pendingRequests.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8" data-testid="pending-requests">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold text-amber-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Document Requests ({pendingRequests.length})</h3>
            </div>
            <div className="space-y-3">
              {pendingRequests.map(req => (
                <div key={req.id} className="bg-white rounded-xl p-4 border border-amber-100 flex items-center justify-between gap-4">
                  <div>
                    <p className="font-medium text-slate-900">{req.message}</p>
                    <p className="text-xs text-slate-500">From: {req.requested_by_name} &middot; {new Date(req.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white rounded-lg" onClick={() => { setUploadType(req.document_type); fileRef.current?.click(); }}
                      data-testid={`upload-for-request-${req.id}`}>
                      <Upload className="w-3 h-3 mr-1" /> Upload
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => handleFulfill(req.id)} data-testid={`fulfill-request-${req.id}`}>
                      <CheckCircle className="w-3 h-3 mr-1" /> Done
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload Section */}
        <div className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 mb-8" data-testid="upload-section">
          <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Upload Document</h2>
          <p className="text-xs text-slate-500 mb-5">
            Already on a GLP-1 from another provider? Select <strong className="text-amber-700">&quot;Prior Prescription&quot;</strong>{" "}
            and upload a photo or PDF of your current script — your prescriber may use it to skip the starter
            dose and move straight to your maintenance strength.
          </p>
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div>
              <Label className="text-sm font-medium text-slate-700">Document Type</Label>
              <Select value={uploadType} onValueChange={setUploadType}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="upload-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(DOC_TYPE_LABELS).filter(([k]) => k !== "prescriber_notes").map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Description (optional)</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. A1C lab from Jan 2026" className="mt-1 rounded-xl" data-testid="upload-description" />
            </div>
            <div className="flex items-end">
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full bg-purple-600 hover:bg-purple-700 text-white px-6 w-full" data-testid="upload-btn">
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Choose File</>}
              </Button>
            </div>
          </div>
          <input ref={fileRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx" onChange={handleUpload} data-testid="file-input" />
          <p className="text-xs text-slate-500 mt-2">Accepted: PDF, JPG, PNG, GIF, WEBP, DOC, DOCX. Max 10MB.</p>
        </div>

        {/* Documents List */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Uploaded Documents</h2>
          <div className="flex gap-2" data-testid="doc-filter">
            {[{ key: "all", label: "All" }, ...Object.entries(DOC_TYPE_LABELS).map(([k, v]) => ({ key: k, label: v.label }))].map((f) => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${filter === f.key ? "bg-purple-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? <div className="text-center py-12 text-slate-400">Loading...</div> :
        filteredDocs.length === 0 ? (
          <div className="bg-white rounded-2xl border border-purple-100 p-12 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="no-documents">
            <FileText className="w-12 h-12 text-purple-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>No Documents Yet</h3>
            <p className="text-slate-500">Upload your first document to get started.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="documents-list">
            {filteredDocs.map(doc => {
              const typeInfo = DOC_TYPE_LABELS[doc.document_type] || DOC_TYPE_LABELS.other;
              const TypeIcon = typeInfo.icon;
              return (
                <div key={doc.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-4 hover:shadow-md transition-all" data-testid={`doc-${doc.id}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl bg-${typeInfo.color}-100 flex items-center justify-center flex-shrink-0`}>
                      <TypeIcon className={`w-5 h-5 text-${typeInfo.color}-600`} />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{doc.original_filename}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <span className={`px-2 py-0.5 rounded-full bg-${typeInfo.color}-50 text-${typeInfo.color}-700`}>{typeInfo.label}</span>
                        <span>{(doc.size / 1024).toFixed(0)} KB</span>
                        <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                        {doc.description && <span>&middot; {doc.description}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)} data-testid={`download-doc-${doc.id}`}>
                      <Download className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(doc.id)} data-testid={`delete-doc-${doc.id}`}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
